import * as Api from '@ViewModels';
import * as React from 'react';
import { toast } from 'sonner';
import { SocialMediaPostViewModel } from '../../../../extViewmodels';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { useVideoAttachmentFromSystemJob, useVideoUploadMutation } from '../SocialMediaImageSearchAndDisplay/hooks';

export function useVideoFileUploader({
	impersonationContext,
	onError,
	post,
}: {
	impersonationContext?: ImpersonationContextViewModel;
	onError?: (err: Api.IOperationResultNoValue) => void;
	post?: SocialMediaPostViewModel;
}) {
	const { schedulePost, updatePost, selectedCreator } = useEditSocialMediaPostContext();

	const [scheduledPost, setScheduledPost] = React.useState<SocialMediaPostViewModel>(post);
	const [draftCreated, setDraftCreated] = React.useState<boolean>(false);
	React.useEffect(() => {
		setDraftCreated(false);
	}, [post]);
	const [jobId, setJobId] = React.useState<string>();
	const userSession = useUserSession();

	const videoUploadMutation = useVideoUploadMutation({
		onError: err => {
			toast.error(err?.systemMessage ?? 'Something went wrong while uploading the video.');
			onError?.(err);
		},
		onSuccess: setJobId,
		impersonationContext,
	});

	const { videoAttachment, errorMessage, resetErrorMessage } = useVideoAttachmentFromSystemJob({
		jobId,
		impersonationContext,
	});
	const isLoading = videoUploadMutation.isPending || (Boolean(jobId) && !videoAttachment && !errorMessage);

	const uploadVideo = React.useCallback(
		async (file: File, videoElm: HTMLVideoElement) => {
			let postId = scheduledPost?.id;

			try {
				if (postId) {
					await updatePost(scheduledPost);
				} else {
					// save draft first
					const [postViewModel] = await schedulePost(null, null, impersonationContext);
					postId = postViewModel.id;
					// store vm so we can return it when done
					setScheduledPost(postViewModel);
					setDraftCreated(true);
				}

				videoUploadMutation.mutate({
					duration: videoElm.duration,
					file,
					forUserId:
						Boolean(selectedCreator?.id) && selectedCreator?.id !== userSession.user?.id
							? selectedCreator?.id
							: undefined,
					height: videoElm.videoHeight,
					postId,
					width: videoElm.videoWidth,
				});
			} catch (ex) {
				onError?.(Api.asApiError(ex));
			}
		},
		[
			impersonationContext,
			onError,
			scheduledPost,
			schedulePost,
			selectedCreator?.id,
			updatePost,
			userSession.user?.id,
			videoUploadMutation,
		]
	);

	React.useEffect(() => {
		// clear out job on success or error
		setJobId(null);
	}, [videoAttachment, errorMessage]);
	return {
		draftCreated,
		errorMessage,
		isLoading,
		resetErrorMessage,
		scheduledPost,
		uploadVideo,
		videoAttachment,
	} as const;
}
